import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Block, OperationTypes } from '../../models/block';
import { TrafficRoutes } from '../../models/traffic-patterns';
import { updateBlockDynamicProperties } from '../../store/game/gameSlice';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Divider } from '@mui/material';
import { ContentTypeChip } from '../block/ContentTypeChip';

interface Props {
  block: Block;
  connectedBlocks: Block[];
  trafficRoutes: TrafficRoutes;
}

export const LoadBalancerSettings: React.FC<Props> = ({ block, connectedBlocks, trafficRoutes }) => {
  const dispatch = useDispatch();
  // routeAssignments: { [assignmentKey]: targetBlockId }
  const [routeAssignments, setRouteAssignments] = useState<any>(block.dynamicProperties?.routeAssignments || {});

  const handleAssign = (assignmentKey: string, targetBlockId: string) => {
    const next = {
      ...routeAssignments,
      [assignmentKey]: targetBlockId
    };
    if (!targetBlockId) {
      delete next[assignmentKey];
    }
    setRouteAssignments(next);
    
    // Auto-save on change
    dispatch(updateBlockDynamicProperties({
      blockId: block.id,
      properties: { routeAssignments: next }
    }));
  };

  if (connectedBlocks.length === 0) {
      return (
          <Typography variant="body2" color="textSecondary">
              Connect this load balancer to other blocks to assign traffic routes.
          </Typography>
      );
  }

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" paragraph>
          Assign globally configured traffic routes to connected output blocks. Routes and content types without an assignment will be distributed evenly among all connected blocks.
      </Typography>
      <Divider sx={{ mb: 2.5 }} />

      <Box sx={{ maxHeight: '420px', overflowY: 'auto', pr: 0.5 }}>
        {trafficRoutes.map(route => {
          return (
            <Box 
              key={route.id} 
              sx={{ 
                mb: 2.5, 
                p: 2, 
                border: '1px solid #e2e8f0', 
                borderRadius: 2.5, 
                bgcolor: '#f8fafc' 
              }}
            >
              <Typography variant="subtitle2" fontWeight="700" color="primary.dark" sx={{ mb: 1.5 }}>
                Route: {route.path}
              </Typography>
              
              {route.contentTypes.map(ct => {
                const flows: { opType: string }[] = [];
                if (ct.readRatio > 0) {
                  flows.push({ opType: OperationTypes.READ });
                }
                if (ct.readRatio < 100) {
                  flows.push({ opType: OperationTypes.WRITE });
                }

                return (
                  <Box key={ct.id} sx={{ mb: 2, pl: 2, borderLeft: '2px solid #cbd5e1', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {flows.map(flow => {
                      const assignmentKey = `${route.id}_${ct.type}_${flow.opType}`;
                      const selectedVal = routeAssignments[assignmentKey] || '';
                      const typeKey = `${flow.opType}-${ct.type}`;
                      
                      return (
                        <Box key={flow.opType} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 2 }}>
                          <Box sx={{ minWidth: 140 }}>
                            <ContentTypeChip typeKey={typeKey} />
                          </Box>
                          <FormControl size="small" sx={{ flexGrow: 1 }}>
                            <InputLabel sx={{ fontSize: '12px' }}>Target Block</InputLabel>
                            <Select
                              value={selectedVal}
                              label="Target Block"
                              onChange={(e) => handleAssign(assignmentKey, e.target.value as string)}
                              sx={{ fontSize: '13px', height: '36px' }}
                            >
                              <MenuItem value="" sx={{ fontSize: '13px' }}>
                                <em>Unassigned (Distribute Evenly)</em>
                              </MenuItem>
                              {connectedBlocks.map(b => (
                                <MenuItem key={b.id} value={b.id} sx={{ fontSize: '13px' }}>
                                  {b.customName || b.id}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
